import { FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import { useAuth } from '@/context/AuthContext';

interface AuthPageProps {
  mode: 'login' | 'register';
}

const ERROR_TEXT: Record<string, string> = {
  invalid_name: 'Digite seu nome.',
  invalid_email: 'Digite um e-mail válido.',
  weak_password: 'A senha precisa ter pelo menos 6 caracteres.',
  email_exists: 'Este e-mail já possui uma conta.',
  invalid_credentials: 'E-mail ou senha incorretos.',
  unknown: 'Não foi possível concluir. Tente novamente.',
};

export default function AuthPage({ mode }: AuthPageProps) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const next = params.get('next') || '/aulas';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'register') await register(name, email, password);
      else await login(email, password);
      navigate(next, { replace: true });
    } catch (err) {
      const code =
        err && typeof err === 'object' && 'code' in err ? String((err as { code?: string }).code) : 'unknown';
      setError(ERROR_TEXT[code] ?? ERROR_TEXT.unknown);
    } finally {
      setBusy(false);
    }
  };

  const alternatePath =
    mode === 'register'
      ? `/entrar?next=${encodeURIComponent(next)}`
      : `/criar-conta?next=${encodeURIComponent(next)}`;

  return (
    <div className="flex min-h-screen flex-col bg-[#fffaf4]">
      <SiteHeader />
      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="grid w-full max-w-5xl overflow-hidden rounded-[28px] border border-orange-100 bg-white shadow-2xl shadow-orange-950/10 md:grid-cols-[0.95fr_1.05fr]">
          <div className="hidden min-h-[560px] bg-gradient-to-br from-[#2a170d] via-[#5a2d13] to-[#d66a12] p-10 text-white md:flex md:flex-col md:justify-between">
            <div>
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-500/20 ring-1 ring-orange-300/30">
                <i className="ri-music-2-fill text-2xl text-orange-300" />
              </span>
              <h1 className="mt-8 font-heading text-4xl font-extrabold leading-tight">
                Seu progresso fica salvo com você.
              </h1>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-orange-50/85">
                Crie uma conta simples para guardar etapas concluídas, notas, tentativas e sua evolução
                na leitura musical.
              </p>
            </div>

            <div className="space-y-3 text-sm text-orange-50/90">
              {[
                'Histórico de exercícios e precisão',
                'Progresso disponível em outros dispositivos',
                'Backup automático do seu aprendizado',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-300" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="p-6 sm:p-8 md:p-10">
            <span className="text-xs font-extrabold uppercase tracking-[0.16em] text-orange-600">
              {mode === 'register' ? 'Criar conta' : 'Bem-vindo de volta'}
            </span>
            <h2 className="mt-2 font-heading text-3xl font-extrabold text-slate-950">
              {mode === 'register' ? 'Comece a praticar' : 'Entrar na sua conta'}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {mode === 'register'
                ? 'Leva menos de um minuto. Seu progresso será salvo automaticamente.'
                : 'Continue exatamente de onde parou.'}
            </p>

            <form onSubmit={submit} className="mt-7 space-y-4">
              {mode === 'register' ? (
                <label className="block">
                  <span className="mb-1.5 block text-sm font-bold text-slate-700">Nome</span>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                    placeholder="Seu nome"
                  />
                </label>
              ) : null}

              <label className="block">
                <span className="mb-1.5 block text-sm font-bold text-slate-700">E-mail</span>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  autoComplete="email"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                  placeholder="voce@email.com"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-bold text-slate-700">Senha</span>
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                  placeholder="Mínimo 6 caracteres"
                />
              </label>

              {error ? (
                <div className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div>
              ) : null}

              <button
                type="submit"
                disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-3.5 text-sm font-extrabold text-white shadow-lg shadow-orange-500/20 transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-play-circle-fill text-lg" />}
                {mode === 'register' ? 'Criar conta e começar' : 'Entrar'}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-600">
              {mode === 'register' ? 'Já tem uma conta?' : 'Ainda não tem conta?'}{' '}
              <Link to={alternatePath} className="font-extrabold text-orange-600 hover:text-orange-700">
                {mode === 'register' ? 'Entrar' : 'Criar conta'}
              </Link>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
